import React, {forwardRef, lazy, useRef, useState} from 'react';
import {observer} from "mobx-react";
import BlockTitle from "../_BlockTitle";
import styles from "./styles/StaffBlock.module.scss"
import CleverSlider from "../../../utilities/CleverSlider/CleverSlider";
import StaffCard from "./StaffCard";
import {blockObjectFactory} from "../../SchoolBlocks/blockObjectFactory";
import {withoutLocaleInURI} from "../../../../internationalization/i18nURI";
import {EditDropDownItem} from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import WatsonApi from "../../../../backends/WatsonApi";
import NotificationManager from "../../../notifications/NotificationManager";
import ViewMoreButton from "../ViewMoreButton";
import {useStores} from "../../../../stores/useStores";

const StaffReorder = lazy(() => import('./StaffReorder'));

export const UserSlider = forwardRef<CleverSlider, {
    users: IUser[] | StaffMemberProfile[],
    handleNext?: () => void
        }>((props, ref) => {
            const {modalStore} = useStores();

            return <CleverSlider
                ref={ref}
                totalSlides={props.users.length}
                containerClassName={styles.cleverSliderContainer}
                childSlideClassName={styles.childSlideContainer}
                trackClassName={styles.trackClassContainer}
                sliderClassName={styles.sliderClassName}
                scrollArrowClassName={styles.scrollArrowClassName}
                height={260}
                handleNext={props.handleNext}
            >
                {props.users.map(staff => {
                    const modalObj = {
                        id: staff.id,
                        type: "schoolBlocksPerson",
                        blockObj: blockObjectFactory("person", {id: staff.id, blockModel: staff} as IPersonBlock),
                    }
                    return (
                        <button key={staff.id} onClick={() => modalStore.addModal(modalObj)}>
                            <div className={styles.slideItem} data-columns={(4)}>
                                <ul>
                                    <StaffCard staff={staff}></StaffCard>
                                </ul>
                            </div>
                        </button>
                    )
                })}
            </CleverSlider>
        }
        )

const StaffBlock = observer((props: {
    blockObj: IStaffTerrace,
    aboveTheFold: boolean,
}) => {
    const {modalStore, organizationStore, userStore} = useStores();
    const [viewMore, setViewMore] = useState(props.blockObj.staff_members.is_more);
    const [page, setPage] = useState(2);
    const [staffMembers, setStaffMembers] = useState(props.blockObj.staff_members.staff);
    const [searchTerm, setSearchTerm] = useState("");
    const [reorderStaff, setReorderStaff] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const cleverSliderRef = useRef<CleverSlider>(null);

    const fetchStaff = async (resetList = false) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        try {
            const q: {
                search?: string,
                department?: string,
                department_id?: string,
                type?: number,
                is_active?: boolean,
                is_staff?: boolean,
                is_superuser?: boolean,
                is_staff_member?: boolean,
                is_active_staff_member?: boolean,
            } = {};
            let organizationPK = organizationStore.currentOrganization.id;
            if (organizationStore.currentOrganization.type === "school" ||
                organizationStore.currentOrganization.type === "district") {
                q.department = 'null';
            }
            else if (organizationStore.currentOrganization.type === "group" ||
                organizationStore.currentOrganization.type === "department" ||
                organizationStore.currentOrganization.type === "section" ||
                organizationStore.currentOrganization.type === "team") {
                organizationPK = organizationStore.organization.id;
                q.department = organizationStore.currentOrganization.id;
                q.type = 2;
            }
            const client = await WatsonApi();
            const resp = await client.apis.organizations.organizations_read_only_staff_member_list({
                organization_pk: organizationPK,
                page: resetList ? 1 :page,
                page_size: props.blockObj.staff_members.page_size,
                deleted: "false",
                ...q,
                ordering: "profile__lname, profile__fname, profile__id",
            });
            setPage(resetList ? 2 : v => v + 1);
            setStaffMembers(prevVals => resetList ? [...resp.obj.data] : [...prevVals, ...resp.obj.data]);
            setViewMore(!!resp.obj.links.next);
        } catch (e) {
            NotificationManager.error(e.message);
        } finally {
            setIsLoading(false);
        }

    }

    const handleSearch = async () => {
        try {
            const q: {
                search?: string,
                department?: string,
                department_id?: string,
                type?: number,
                is_active?: boolean,
                is_staff?: boolean,
                is_superuser?: boolean,
                is_staff_member?: boolean,
                is_active_staff_member?: boolean,
            } = {};
            let organizationPK = organizationStore.currentOrganization.id;

            if (organizationStore.currentOrganization.type === "school" ||
                organizationStore.currentOrganization.type === "district") {
                q.department = 'null';
            }
            else if (organizationStore.currentOrganization.type === "group" ||
                organizationStore.currentOrganization.type === "department" ||
                organizationStore.currentOrganization.type === "section" ||
                organizationStore.currentOrganization.type === "team") {
                organizationPK = organizationStore.organization.id;
                q.department_id = organizationStore.currentOrganization.id;
                q.type = 2;
            }
            const client = await WatsonApi();
            const resp = await client.apis.organizations.organizations_read_only_staff_member_list({
                organization_pk: organizationPK,
                search: searchTerm,
                ...q,
                deleted: "false",
                ordering: "profile__lname, profile__fname, profile__id",
            });
            if (cleverSliderRef.current) {
                cleverSliderRef.current.resetIndex(); //Reset index in CleverSlider so search results are not offscreen
            }
            setStaffMembers([...resp.obj.data]);
        } catch (e) {
            NotificationManager.error(e.message);
        }
    }

    function onClick () {
        modalStore.addModal({
            type: "syncGroupsModal",
        });
    }

    const staffBlockDropDownItems: EditDropDownItem[] = [
        {
            title: 'Add Staff',
            icon: <FontAwesome prefix={'fas'} fixedWidth className="fa-plus"/>,
            onClick: onClick,
        },
        {
            title: 'Reorder Staff',
            icon: <FontAwesome prefix={'fas'} fixedWidth className="fa-arrows-alt"/>,
            onClick: () => {
                if (reorderStaff) {
                    fetchStaff(true);
                }
                setReorderStaff(v => !v)

            },
            hideButtonSelector: true,
        },
    ]


    return (
        <article>
            <BlockTitle
                blockObj={props.blockObj}
                aboveTheFold={props.aboveTheFold}
                dropDownItems={["section", "group", "department"].includes(organizationStore.currentOrganization.type) && userStore.isAdmin && staffBlockDropDownItems}
                showViewMore={organizationStore.currentOrganization.id === organizationStore.organization.id}
                viewMoreButtonUrl={withoutLocaleInURI("/staff")}
            />
            {reorderStaff ? (
                <StaffReorder
                        blockId={props.blockObj.id}
                        onComplete={async () => {
                            setReorderStaff(false);
                            await fetchStaff(true)
                        }}
                    />
                ) :
                <div className={styles.container}>
                    <div className={styles.searchContainer}>
                        <div>
                            <input
                            type="search"
                            className={styles.searchbar}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search Staff Members"
                            onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    await handleSearch();
                                }
                            }}
                        />
                            <button onClick={handleSearch}>
                                <FontAwesome prefix={'fas'} name={'fa-search'}/>
                            </button>
                        </div>
                    </div>
                    {(staffMembers?.length > 0) ? <UserSlider
                    ref={cleverSliderRef}
                    users={staffMembers}
                    handleNext={viewMore ? fetchStaff : undefined}
                /> : <div className={styles.noStaff}>No Staff Members found.</div>}
                </div>}
            {organizationStore.currentOrganization.type !== 'department' && <div className={styles.viewMore}>
                <ViewMoreButton
                    url={withoutLocaleInURI("/staff")}
                />
            </div>}
        </article>
    );
})

export default StaffBlock;
